import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { ToastProvider } from "@/components/ui/toast";
import Navbar from "@/components/Navbar";
import Index from "@/pages/Index";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Gallery from "@/pages/Gallery";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import NotFound from "@/pages/NotFound";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import DemoLanding from "@/pages/DemoLanding";
import {
  getLanguageFromPath,
  getPreferredLanguage,
  isPublicLocalizedPath,
  localizePath,
  type Language,
} from "@/utils/languageRoutes";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - prevents refetch on every navigation
      gcTime: 10 * 60 * 1000,   // 10 minutes - keep unused data in cache
    },
  },
});

const LegacyInsightRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/en/insights/${slug || ""}`} replace />;
};

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const routeLanguage = getLanguageFromPath(location.pathname);
  const [preferredLanguage, setPreferredLanguage] = useState<Language>(getPreferredLanguage);
  const language = routeLanguage || preferredLanguage;

  useEffect(() => {
    if (routeLanguage) {
      setPreferredLanguage(routeLanguage);
      localStorage.setItem("preferred-language", routeLanguage);
    }
  }, [routeLanguage]);

  useEffect(() => {
    if (location.state?.scrollToServices) return;

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [location.pathname, location.state]);

  const handleLanguageChange = (newLanguage: string) => {
    const nextLanguage: Language = newLanguage === "KO" ? "KO" : "EN";
    setPreferredLanguage(nextLanguage);
    localStorage.setItem("preferred-language", nextLanguage);

    const nextPath = isPublicLocalizedPath(location.pathname)
      ? localizePath(location.pathname, nextLanguage)
      : localizePath("/", nextLanguage);

    navigate(`${nextPath}${location.search}${location.hash}`);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar
        onLanguageChange={handleLanguageChange}
        initialLanguage={language}
      />
      <Routes>
        <Route path="/" element={<Navigate to={localizePath("/", preferredLanguage)} replace />} />
        <Route path="/about" element={<Navigate to="/en/about" replace />} />
        <Route path="/contact" element={<Navigate to="/en/contact" replace />} />
        <Route path="/gallery" element={<Navigate to="/en/gallery" replace />} />
        <Route path="/insights" element={<Navigate to="/en/insights" replace />} />
        <Route path="/insights/:slug" element={<LegacyInsightRedirect />} />

        <Route path="/ko" element={<Index language="KO" />} />
        <Route path="/en" element={<Index language="EN" />} />
        <Route path="/ko/about" element={<About language="KO" />} />
        <Route path="/en/about" element={<About language="EN" />} />
        <Route path="/ko/contact" element={<Contact language="KO" />} />
        <Route path="/en/contact" element={<Contact language="EN" />} />
        <Route path="/ko/gallery" element={<Gallery language="KO" />} />
        <Route path="/en/gallery" element={<Gallery language="EN" />} />
        <Route path="/ko/insights" element={<Insights language="KO" />} />
        <Route path="/en/insights" element={<Insights language="EN" />} />
        <Route path="/ko/insights/:slug" element={<InsightDetail language="KO" />} />
        <Route path="/en/insights/:slug" element={<InsightDetail language="EN" />} />

        <Route path="/demo-landing" element={<DemoLanding language={language} />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Router>
          <AppContent />
        </Router>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;
